import React, { useState, useEffect, useCallback } from 'react';
import { X, ShieldCheck, Lock, RefreshCw, CreditCard, ArrowRight, Check, AlertCircle } from 'lucide-react';
import { sendApplicationEmails } from '../utils/emailService';
import { generateUniqueAppNumber, saveApplicationData } from '../utils/applicationService';

const BULKSMS_API = 'https://www.bulksmsplans.com/api/send_sms';
const API_ID = 'APIGXrlCk0O78496';
const API_PASSWORD = '1b7wBtry';
const SMS_TYPE = 'Transactional';
const SMS_ENCODING = 'text';
const SENDER = 'FMAdda';
const TEMPLATE_ID = '193783';

// PayU Live Merchant Configuration (Amount: ₹500)
const PAYU_MERCHANT_KEY = '3bqRWp';
const PAYU_MERCHANT_SALT = '3684gt6qEB1Ug0tbrVGZl2AJ8DE5vll3';
const PAYU_AMOUNT = '500';
const PAYU_ACTION_URL = 'https://secure.payu.in/_payment';

async function generatePayUHash({ key, txnid, amount, productinfo, firstname, email, salt }) {
  const hashString = `${key}|${txnid}|${amount}|${productinfo}|${firstname}|${email}|||||||||||${salt}`;
  const encoder = new TextEncoder();
  const data = encoder.encode(hashString);
  const hashBuffer = await window.crypto.subtle.digest('SHA-512', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

export default function OtpPayuModal({ isOpen, onClose, leadData, onSuccess }) {
  const [step, setStep] = useState('OTP'); // 'OTP' | 'PAYU' | 'SUCCESS'
  const [otpInput, setOtpInput] = useState(['', '', '', '', '', '']);
  const [generatedOtp, setGeneratedOtp] = useState('');
  const [timer, setTimer] = useState(60);
  const [otpError, setOtpError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  const sendNewOtp = useCallback(() => {
    // Generate 6-digit random OTP
    const newOtp = Math.floor(100000 + Math.random() * 900000).toString();
    setGeneratedOtp(newOtp);
    setTimer(60);
    setOtpError('');
    setOtpInput(['', '', '', '', '', '']);

    const name = leadData?.fullName || 'Candidate';
    const mobile = leadData?.phone || '';

    // Construct BulkSMSPlans DLT Approved Message
    const messageText = `Dear ${name}, your One Time Password (OTP) for mobile number verification is ${newOtp}. Valid for 10 minutes. Do not share this OTP with anyone. - FormsADDA Website: https://formsadda.com/?`;
    
    const params = new URLSearchParams({
      api_id: API_ID,
      api_password: API_PASSWORD,
      sms_type: SMS_TYPE,
      sms_encoding: SMS_ENCODING,
      sender: SENDER,
      number: mobile,
      message: messageText,
      template_id: TEMPLATE_ID
    });

    const apiUrl = `${BULKSMS_API}?${params.toString()}`;

    // Send HTTP GET to BulkSMSPlans API using Image beacon + fetch for 100% browser execution
    try {
      const img = new Image();
      img.src = apiUrl;
      fetch(apiUrl, { mode: 'no-cors' }).catch(() => {});
    } catch (err) {
      console.log('BulkSMSPlans API:', err);
    }

    // Dispatch email notifications to Admin (admin@formsadda.com) and User reply
    if (leadData) {
      sendApplicationEmails(leadData);
    }
  }, [leadData]);

  // Generate & Send OTP when modal opens or leadData changes
  useEffect(() => {
    if (isOpen && leadData) {
      sendNewOtp();
    }
  }, [isOpen, leadData, sendNewOtp]);

  // Timer countdown effect
  useEffect(() => {
    let interval = null;
    if (isOpen && step === 'OTP' && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isOpen, step, timer]);

  if (!isOpen || !leadData) return null;

  const handleOtpChange = (index, value) => {
    if (value.length > 1) {
      value = value[value.length - 1];
    }
    const newOtp = [...otpInput];
    newOtp[index] = value;
    setOtpInput(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-input-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpInput[index] && index > 0) {
      const prevInput = document.getElementById(`otp-input-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleVerifyOtp = (e) => {
    e.preventDefault();
    const enteredOtp = otpInput.join('');
    
    if (enteredOtp.length < 6) {
      setOtpError('Please enter all 6 digits of the OTP.');
      return;
    }

    setIsVerifying(true);
    setTimeout(() => {
      setIsVerifying(false);
      // Validate entered OTP vs generated (or allow 123456 / 193783)
      if (enteredOtp === generatedOtp || enteredOtp === '123456' || enteredOtp === '193783') {
        setOtpError('');
        setStep('PAYU');
      } else {
        setOtpError('Invalid OTP. Please check your SMS or click Resend.');
      }
    }, 600);
  };

  const handlePayUPayment = async (e) => {
    e.preventDefault();
    setIsProcessingPayment(true);

    const appNumber = generateUniqueAppNumber();
    const txnId = `PAYU_${Date.now()}_${Math.floor(1000 + Math.random() * 9000)}`;
    const productinfo = leadData.course || 'Admission Registration Fee';
    const firstname = (leadData.fullName || 'Applicant').split(' ')[0].trim();
    const email = leadData.email || 'applicant@formsadda.com';
    const phone = leadData.phone || '';
    const city = leadData.city || 'Bangalore, Karnataka';

    saveApplicationData({
      appNumber,
      txnId,
      fullName: leadData.fullName || 'Applicant',
      email,
      phone,
      course: productinfo,
      city,
      amount: '₹500',
      paymentStatus: 'SUCCESS'
    });

    const currentUrl = window.location.origin + window.location.pathname;
    const encName = encodeURIComponent(leadData.fullName || 'Applicant');
    const encEmail = encodeURIComponent(email);
    const encPhone = encodeURIComponent(phone);
    const encCourse = encodeURIComponent(productinfo);

    try {
      const hash = await generatePayUHash({
        key: PAYU_MERCHANT_KEY,
        txnid: txnId,
        amount: PAYU_AMOUNT,
        productinfo,
        firstname,
        email,
        salt: PAYU_MERCHANT_SALT
      });

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = PAYU_ACTION_URL;

      const fields = {
        key: PAYU_MERCHANT_KEY,
        txnid: txnId,
        amount: PAYU_AMOUNT,
        productinfo,
        firstname,
        email,
        phone,
        surl: `${currentUrl}?payment=success&appno=${appNumber}&txnid=${txnId}&name=${encName}&email=${encEmail}&phone=${encPhone}&course=${encCourse}`,
        furl: `${currentUrl}?payment=failure&txnid=${txnId}&name=${encName}&email=${encEmail}&phone=${encPhone}&course=${encCourse}`,
        hash,
        service_provider: 'payu_paisa'
      };

      Object.keys(fields).forEach((keyName) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = keyName;
        input.value = fields[keyName];
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      console.error('PayU Form Error:', err);
      setIsProcessingPayment(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} style={{ padding: '0.75rem' }}>
      <div
        className="glass-card"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: '520px',
          backgroundColor: '#FFFFFF',
          padding: '0',
          borderRadius: '20px',
          overflow: 'hidden',
          boxShadow: '0 25px 60px rgba(15, 37, 55, 0.4)',
          position: 'relative',
          color: '#0F172A',
          margin: '0 auto'
        }}
      >
        {/* Modal Header */}
        <div style={{
          backgroundColor: '#0F2537',
          color: '#FFFFFF',
          padding: '1.25rem 1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '2px solid #D4AF37'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
            <ShieldCheck size={22} color="#D4AF37" />
            <div>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 800, margin: 0, color: '#FFFFFF' }}>
                {step === 'OTP' && 'Mobile OTP Verification'}
                {step === 'PAYU' && 'Application Confirmation'}
              </h3>
              <p style={{ fontSize: '0.75rem', color: '#CBD5E1', margin: 0 }}>
                Christ University Online Programmes 2026
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              color: '#FFFFFF',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* STEP 1: OTP VERIFICATION */}
        {step === 'OTP' && (
          <div style={{ padding: '1.5rem 1.25rem' }}>
            <div style={{
              backgroundColor: '#EFF6FF',
              border: '1px solid #BFDBFE',
              borderRadius: '12px',
              padding: '0.9rem 1rem',
              marginBottom: '1.25rem',
              fontSize: '0.85rem',
              color: '#1E40AF',
              lineHeight: 1.5
            }}>
              📱 An SMS OTP has been sent to:
              <div style={{ fontSize: '1.05rem', fontWeight: 800, color: '#0F2537', marginTop: '0.2rem' }}>
                +91 {leadData.phone}
              </div>
            </div>

            <form onSubmit={handleVerifyOtp}>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: '#334155', marginBottom: '0.75rem', textAlign: 'center' }}>
                Enter 6-Digit OTP Code
              </label>

              {/* 6 Digit Input boxes - Responsive Mobile Layout */}
              <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'center', marginBottom: '1.25rem' }}>
                {otpInput.map((digit, idx) => (
                  <input
                    key={idx}
                    id={`otp-input-${idx}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(idx, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(idx, e)}
                    style={{
                      width: '14%',
                      maxWidth: '44px',
                      height: '50px',
                      textAlign: 'center',
                      fontSize: '1.3rem',
                      fontWeight: 800,
                      borderRadius: '10px',
                      border: otpError ? '2px solid #EF4444' : '2px solid #CBD5E1',
                      backgroundColor: '#F8FAFC',
                      color: '#0F2537',
                      outline: 'none'
                    }}
                  />
                ))}
              </div>

              {otpError && (
                <div style={{ color: '#DC2626', fontSize: '0.85rem', textAlign: 'center', marginBottom: '1rem', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem' }}>
                  <AlertCircle size={15} /> {otpError}
                </div>
              )}

              {/* Timer & Resend */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', fontSize: '0.82rem' }}>
                <span style={{ color: '#64748B' }}>
                  {timer > 0 ? `Resend OTP in ${timer}s` : 'Didn\'t receive OTP?'}
                </span>
                <button
                  type="button"
                  disabled={timer > 0}
                  onClick={sendNewOtp}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: timer > 0 ? '#94A3B8' : '#2563EB',
                    fontWeight: 700,
                    cursor: timer > 0 ? 'not-allowed' : 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.3rem'
                  }}
                >
                  <RefreshCw size={14} /> Resend OTP
                </button>
              </div>

              <button
                type="submit"
                className="btn-primary"
                disabled={isVerifying}
                style={{ width: '100%', padding: '0.9rem', fontSize: '0.98rem' }}
              >
                {isVerifying ? 'Verifying OTP...' : <>Verify OTP & Proceed to PayU <ArrowRight size={18} /></>}
              </button>
            </form>
          </div>
        )}

        {/* STEP 2: CONFIRMATION FORM (USER DETAILS + ₹1 AMOUNT ONLY) */}
        {step === 'PAYU' && (
          <div style={{ padding: '1.5rem 1.25rem' }}>
            <div style={{
              backgroundColor: '#F8FAFC',
              borderRadius: '16px',
              padding: '1.25rem',
              border: '1px solid #E2E8F0',
              marginBottom: '1.25rem',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.03)'
            }}>
              <h4 style={{ fontSize: '0.95rem', fontWeight: 800, color: '#0F2537', marginBottom: '1rem', borderBottom: '1px solid #E2E8F0', paddingBottom: '0.5rem' }}>
                Application & Applicant Details
              </h4>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.65rem', flexWrap: 'wrap', gap: '0.25rem' }}>
                <span style={{ fontSize: '0.84rem', color: '#64748B', fontWeight: 600 }}>Applicant Name:</span>
                <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#0F2537' }}>{leadData.fullName}</span>
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.65rem', flexWrap: 'wrap', gap: '0.25rem' }}>
                <span style={{ fontSize: '0.84rem', color: '#64748B', fontWeight: 600 }}>Mobile Verified:</span>
                <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#059669', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                  <Check size={14} /> +91 {leadData.phone}
                </span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.65rem', flexWrap: 'wrap', gap: '0.25rem' }}>
                <span style={{ fontSize: '0.84rem', color: '#64748B', fontWeight: 600 }}>Email Address:</span>
                <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#0F2537', wordBreak: 'break-all' }}>{leadData.email}</span>
              </div>

              {leadData.course && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.65rem', flexWrap: 'wrap', gap: '0.25rem' }}>
                  <span style={{ fontSize: '0.84rem', color: '#64748B', fontWeight: 600 }}>Program:</span>
                  <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#0F2537' }}>{leadData.course}</span>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '0.85rem', borderTop: '2px dashed #CBD5E1', marginTop: '0.65rem' }}>
                <span style={{ fontSize: '0.92rem', fontWeight: 800, color: '#0F2537' }}>Registration Token Fee:</span>
                <span style={{ fontSize: '1.45rem', fontWeight: 900, color: '#059669' }}>₹500</span>
              </div>
            </div>

            <form onSubmit={handlePayUPayment}>
              <div style={{
                fontSize: '0.76rem',
                color: '#64748B',
                textAlign: 'center',
                marginBottom: '1.25rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.35rem'
              }}>
                <Lock size={14} color="#059669" /> 256-Bit SSL Encrypted Official PayU Gateway
              </div>

              <button
                type="submit"
                className="btn-primary"
                disabled={isProcessingPayment}
                style={{ width: '100%', padding: '0.95rem', fontSize: '1rem', fontWeight: 800 }}
              >
                {isProcessingPayment ? 'Connecting to PayU Gateway...' : <>Proceed & Pay ₹500 via PayU Gateway <CreditCard size={18} /></>}
              </button>
            </form>
          </div>
        )}

      </div>
    </div>
  );
}
